import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCerRifiutiProdotti, createCerRifiutoProdotto, updateCerRifiutoProdotto,
  type CerRifiutoProdotto, type Tipologia, type Classificazione,
  type CommaReverseCharge,
} from '../../api/settings';
import type { RegimeIva } from '../../api/fatture';
import { useAuthStore } from '../../store/authStore';
import SettingsCrudSection, { type FieldDef, type FieldOption } from './SettingsCrudSection';

export default function CerRifiutiProdottiSettings() {
  const { t } = useTranslation();
  // Il regime IVA e' un parametro FISCALE: il backend risponde 403 a chi non e'
  // {admin, DIR} (stesso gate di sezionale/aliquota trasporto). RT resta libero
  // sul resto del catalogo, quindi il campo si disabilita e si OMETTE dal
  // payload: mandarlo comunque farebbe fallire con 403 anche la correzione di
  // un nome.
  const role = useAuthStore((s) => s.user?.role);
  const canEditFiscal = role === 'admin' || role === 'DIR';
  const TIPOLOGIE: FieldOption[] = [{ value: 'urbano', label: t('settings.cer_ingresso.tip_urbano') }, { value: 'speciale', label: t('settings.cer_ingresso.tip_speciale') }];
  const CLASSIFICAZIONI: FieldOption[] = [{ value: 'non_pericoloso', label: t('settings.cer_ingresso.cls_non_pericoloso') }, { value: 'pericoloso', label: t('settings.cer_ingresso.cls_pericoloso') }];
  // V72: regime IVA con cui nasce la riga di una bozza di vendita per questo
  // rifiuto. E' un DEFAULT, non un vincolo: il regime effettivo e' snapshottato
  // sull'uscita, quindi cambiare qui non riscrive una bozza gia' aperta.
  const REGIMI: FieldOption[] = [
    { value: 'reverse_charge', label: t('settings.cer_rifiuti.regime_reverse_charge') },
    { value: 'iva_ordinaria', label: t('settings.cer_rifiuti.regime_iva_ordinaria') },
  ];
  const regimeLabel = (v: string | null | undefined) =>
    REGIMI.find((r) => r.value === v)?.label ?? '—';
  // V73: comma dell'art. 74 che decide la DICITURA in fattura. Fiscale come il
  // regime, stesso gate e stessa omissione dal payload. Configurabile e non
  // dedotto dal CER: il silicio e' un metalloide e va verificato caso per caso.
  const COMMI: FieldOption[] = [
    { value: '7', label: t('settings.cer_rifiuti.comma_7') },
    { value: '8', label: t('settings.cer_rifiuti.comma_8') },
  ];
  // A VIDEO solo «Comma 7» / «Comma 8»: l'elenco dei materiali e' lungo, uguale
  // su ogni riga e troncato comunque, quindi non diceva nulla e si portava via
  // 224px di tabella. Resta nel select della modifica, che e' dove serve a
  // SCEGLIERE. Non il solo numero: l'intestazione puo' finire fuori schermo
  // quando la pagina scorre, e un «7» nudo in mezzo a colonne di parole si
  // legge come una quantita', non come il rimando a un comma di legge.
  // Mappa STATICA e non una chiave composta a runtime: `check-i18n` legge le
  // chiavi dal codice, e una chiave interpolata gli risulta un prefisso
  // dinamico da mettere in allowlist — cioe' si spegnerebbe il controllo su
  // TUTTO il file per due stringhe.
  const COMMI_BREVI: Record<string, string> = {
    '7': t('settings.cer_rifiuti.comma_7_breve'),
    '8': t('settings.cer_rifiuti.comma_8_breve'),
  };
  const commaLabelBreve = (v: string | null | undefined) =>
    COMMI_BREVI[v ?? ''] ?? '—';
  const [items, setItems] = useState<CerRifiutoProdotto[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try { setItems(await getCerRifiutiProdotti(false)); } finally { setLoading(false); }
    })();
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const fields: FieldDef[] = [
    { key: 'cer', label: t('settings.cer_fields.cer'), kind: 'cer', required: true, placeholder: t('settings.cer_rifiuti.cer_placeholder') },
    { key: 'nome_descrittivo', label: t('settings.cer_fields.descrizione'), kind: 'text' },
    { key: 'tipologia', label: t('settings.cer_fields.tipologia'), kind: 'select', required: true, options: TIPOLOGIE, col: 'w-24',
      display: (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '—') },
    { key: 'classificazione', label: t('settings.cer_fields.classificazione'), kind: 'select', required: true, options: CLASSIFICAZIONI, col: 'w-32',
      display: (v) => (v ? v.replace('_', ' ') : '—') },
    { key: 'regime_iva', label: t('settings.cer_fields.regime_iva'), kind: 'select', required: canEditFiscal,
      readOnly: !canEditFiscal, options: REGIMI, col: 'w-40', display: regimeLabel },
    // w-28: la larghezza la detta l'INTESTAZIONE («Comma art. 74»), non piu' il
    // valore. Con w-56 la somma delle colonne fisse superava lo spazio della
    // pagina (max-w-6xl) e la descrizione restava a ~90px: header sovrapposti e
    // colonna Azioni tagliata fuori a destra.
    { key: 'comma_reverse_charge', label: t('settings.cer_fields.comma_reverse_charge'), kind: 'select',
      required: canEditFiscal, readOnly: !canEditFiscal, options: COMMI, col: 'w-28',
      display: commaLabelBreve },
  ];

  return (
    <SettingsCrudSection<CerRifiutoProdotto>
      description={t('settings.cer_rifiuti.descrizione')}
      fields={fields}
      rows={items}
      loading={loading}
      reload={reload}
      testidPrefix="cer-rp"
      entityLabel={t('settings.cer_rifiuti.entita')}
      emptyMsg={t('settings.cer_comune.nessun_cer')}
      isIncomplete={(it) => !!it.attivo && (!it.tipologia || !it.classificazione)}
      onCreate={async (v) => {
        await createCerRifiutoProdotto({
          cer: v.cer.trim(), nome_descrittivo: v.nome_descrittivo.trim() || null,
          tipologia: v.tipologia as Tipologia, classificazione: v.classificazione as Classificazione,
          ...(canEditFiscal ? {
            regime_iva: v.regime_iva as RegimeIva,
            comma_reverse_charge: Number(v.comma_reverse_charge) as CommaReverseCharge,
          } : {}),
        });
      }}
      onUpdate={async (id, v) => {
        await updateCerRifiutoProdotto(id, {
          cer: v.cer.trim(),
          nome_descrittivo: v.nome_descrittivo.trim() || null,
          tipologia: v.tipologia as Tipologia, classificazione: v.classificazione as Classificazione,
          ...(canEditFiscal ? {
            regime_iva: v.regime_iva as RegimeIva,
            comma_reverse_charge: Number(v.comma_reverse_charge) as CommaReverseCharge,
          } : {}),
        });
      }}
      onToggle={async (it) => {
        if (!it.attivo && (!it.tipologia || !it.classificazione)) {
          // Errore LOCALE, non venuto dalla rete: prende la forma di una
          // risposta API perché il gestore comune (SettingsCrudSection →
          // apiErrorMessage) legge di lì. Nessun marcatore axios richiesto.
          throw { response: { data: { detail: t('settings.cer_ingresso.errore_riattiva', { cer: it.cer }) } } };
        }
        await updateCerRifiutoProdotto(it.id, { attivo: !it.attivo });
      }}
    />
  );
}
